import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    OnDestroy,
} from '@angular/core';
import {ApiService} from "../../services/api/api.service";
import {SeriesDataService} from '../../services/series_data.service';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {EventType} from "../../_models/event-type";
import {catchError, concatMap, finalize, first, map, switchMap, take, takeUntil, tap} from "rxjs/operators";
import {forkJoin, Observable, of, Subject, throwError} from "rxjs";
import * as utils from "../../lib/utils";
import {EventService} from '../../services/event.service';
import {MatTabChangeEvent, MatTabGroup} from '@angular/material/tabs';
import {FormDialogService} from "../../services/form-dialog.service";
import {ComponentType} from "../../_models/component-type";
import {Series} from "../../_models/series";
import {SingleResponse} from "../../services/api/response-types";
import {SaveService} from "../../services/save/save.service";
import {EventTypeFormDialogService} from "./event-type-form-dialog.service";
import {RelationshipApiMappingService} from "../../data/relationship-api-mapping.service";
import {RelMappingResult} from "../../_typing/relationship-api-mapping";
import {getManyRelationWithIdFilter} from "../../services/api/filter_utils";
import {NotificationService} from "../../services/notification.service";
import {HttpErrorHandler} from "../../services/http-error-handler.service";
import {ModelID} from "../../_typing/generic-types";
import {CdkDrag, CdkDragHandle} from "@angular/cdk/drag-drop";
import {SharedModule} from "../../shared/modules/shared-module.module";
import {SelectManySearchBaseComponent} from "../../components/select-search-api/many/select-many-search-base.component";
import {SelectManySearchComponent} from "../../components/select-search/select-many-search.component";
import {
    ConstantPropertyTableFormComponent
} from "../constant-property-table-form/constant-property-table-form.component";
import {RouterLink} from "@angular/router";
import {cloneDeep, merge} from "lodash";

export interface EventTypeDialogData {
    event_type: any;
    series_list: any[];
    component_types_list: ComponentType[];
    constant_property: any;
}

@Component({
    selector: 'event-type-form',
    templateUrl: './event-type-form-dialog.component.html',
    styleUrls: ['./event-type-form-dialog.component.less'],
    encapsulation: ViewEncapsulation.None,
    providers: [SaveService],
    imports: [
        SharedModule,
        CdkDrag,
        CdkDragHandle,
        SelectManySearchBaseComponent,
        SelectManySearchComponent,
        ConstantPropertyTableFormComponent,
        RouterLink
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class EventTypeFormDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    added_component_types: ComponentType[];
    removed_component_types: ComponentType[];
    et_ct_list: any[] = [];
    select_component_types: ComponentType[];
    // Per-dialog-session bookkeeping for CTs already reconciled with the server.
    // The SelectMany child's items_added/items_removed are cumulative deltas with no
    // reset on Apply, so we use these to drop already-saved entries from the next
    // saveMany call and avoid duplicate-link failures on the unique
    // (event_type_id, component_type_id) constraint.
    private readonly _sessionLinkedCtIds = new Set<string>();
    private readonly _sessionUnlinkedCtIds = new Set<string>();
    selected_component_types_filter: any[];
    selected_component_types_reverse_filter: any[];
    series_dict: { [id: string]: Series } = {};

    series_list: any[];
    saved_event_type: EventType;
    event_type: EventType;
    et_id: string;
    constant_property: any;
    saving: boolean;

    @ViewChild('event_type_tabs') event_type_tabs: MatTabGroup;
    private readonly onDestroy = new Subject<void>();

    constructor(private api: ApiService,
                public dialogRef: MatDialogRef<EventTypeFormDialogComponent>,
                @Inject(MAT_DIALOG_DATA) public data: EventTypeDialogData,
                private seriesData: SeriesDataService,
                public eventService: EventService,
                public dialog: MatDialog,
                private notification: NotificationService,
                private formDialogService: FormDialogService,
                private saveService: SaveService,
                private cps: EventTypeFormDialogService,
                private relMapping: RelationshipApiMappingService,
                private changeDetection: ChangeDetectorRef,
                private errorHandler: HttpErrorHandler) {

        this.et_id = this.data.event_type?.id;
    }

    ngOnInit() {
        this._setComponentTypeFilters(this.et_id);
        // TODO:page
        const $series = this.api.series_light.searchMany().pipe(
            tap(result => {
                this.series_list = result.data;
                this.series_list.forEach(s => this.series_dict[s.id] = s);
            })
        );

        let $obs = [$series];

        const $lists = forkJoin($obs).pipe(
            first(), takeUntil(this.onDestroy)
        );
        if (this.et_id) {
            this.api.event_type.getById(this.et_id).pipe(
                switchMap(result => {
                    this.event_type = result.data;
                    // Keep a copy to pass back to the table on close without save
                    this.saved_event_type = utils.deepCopy(this.event_type);
                    return $lists;
                }), first(), takeUntil(this.onDestroy))
                .subscribe(() => this.changeDetection.markForCheck());
            // Note that dialog opening from event_type table has event relationships already mapped
        } else {
            this.event_type = new EventType();
            this.saved_event_type = utils.deepCopy(this.event_type);
            $lists.subscribe(() => this.changeDetection.markForCheck());
        }

    }

    ngAfterViewInit() {
        if (this.data.constant_property && this.event_type_tabs) {
            setTimeout(() => {
                this.event_type_tabs.selectedIndex = 2;
                this.changeDetection.markForCheck();
            }, 500)
        }
    }

    editSeries(series) {
        const ctrl = this;
        let $series_full = this.api.series.getById(series.id).toPromise();
        $series_full.then(returned => {
            let series_full = returned.data;
            ctrl.seriesData.upsertSeries(null, series_full).afterClosed().pipe(takeUntil(this.onDestroy)).subscribe((series) => {
                if (series) {
                    let updated_series;
                    if (series.series) {
                        updated_series = series.series;
                    } else {
                        updated_series = series;
                    }
                    ctrl.event_type.relationships.series_list.data.map(s => {
                        if (s.id === series.id) {
                            s = updated_series;
                        }
                    });
                    this.changeDetection.markForCheck();
                }
            });
        });
    }

    editComponentType(component_type) {
        const dialogRef = this.formDialogService.editComponentType(component_type);
        dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
            if (result) {
                if (component_type) {
                    component_type = result;
                }
                this.changeDetection.markForCheck();
            }
        });
    }

    onTabChanged(event: MatTabChangeEvent) {
    }

    save() {
        if (this.saving) return;
        this.saveEventType().pipe(tap(result => {
            if (result) {
                this.onCloseClick();
            }
        }), catchError(err => of(null))).subscribe();
    }

    apply() {
        if (this.saving) return;
        this.saveEventType().subscribe(() => {
            this.changeDetection.markForCheck()
        });
    }

    saveEventType(): Observable<any> {
        this.saving = true;
        this.dialogRef.disableClose = true;

        // Guard the synchronous setup: if deepCopy/cleanup or an obs builder throws,
        // the pipe below never starts and its finalize never runs — which would leave
        // `saving` and `disableClose` stuck true and lock the dialog. Reset both before
        // re-throwing as a stream error so the caller's catchError can handle it.
        let $promise: Observable<any>;
        try {
            const data = utils.deepCopy(this.event_type);
            EventType.cleanup(data);
            $promise = this.event_type.id
                ? this.api.event_type.obsPatch(data)
                : this.api.event_type.obsSave(data);
        } catch (e) {
            this.saving = false;
            this.dialogRef.disableClose = false;
            return throwError(() => e);
        }
        // takeUntil intentionally omitted: with disableClose blocking ESC and backdrop,
        // the dialog can't close through normal UX. On programmatic destroy (navigation,
        // parent teardown), letting the in-flight CT-link saveMany finish is safer than
        // cancelling mid-batch — ngOnDestroy surfaces a warning if that happens.
        return $promise.pipe(first(),
            tap((result: SingleResponse<EventType>) => {
                if (result) {
                    this.event_type.id = result.data.id;

                    // this allows table to reset event_type if user clicks apply then close and/or close without saving
                    this.saved_event_type = utils.deepCopy(this.event_type);
                    this.notification.openSuccess('Event type saved.');
                }
                this._setComponentTypeFilters(this.event_type.id);
                this.saveService.emitSave();
            }),
            concatMap(eventTypeResult =>
                this.saveManyComponentTypes().pipe(map(ctResult =>
                    ctResult && ctResult.failures.length > 0 ? null : eventTypeResult))),
            catchError(err => {
                const html = err.error;
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const message = doc.getElementsByTagName('p')[0]?.textContent?.trim();
                if (message) {
                    const noticeMessage: string = `${message} Please use a different name.`
                    this.notification.openError(noticeMessage, 2000);
                } else {
                    this.errorHandler.checkErrors(err, "Unable to save event type. ", true, true);
                }
                return of(null);
            }),
            finalize(() => {
                this.saving = false;
                this.dialogRef.disableClose = false;
            })
        );
    }

    trackComponentTypes(added?: ComponentType[], removed?: ComponentType[]): Observable<any> {
        const ids = this.removed_component_types?.map(ct => ct.id);
        if (!ids) {
            return of([]);
        }
        return this.cps.getEventTypeComponentTypesByComponentTypesandEventType(this.event_type.id, this.removed_component_types.map(ct => ct.id))
            .pipe(tap(result => {
                this.et_ct_list = result.data;
            }));
    }

    saveManyComponentTypes(): Observable<RelMappingResult | null> {
        // Drop deltas already reconciled with the server on a prior Apply,
        // so a retry after partial failure doesn't re-submit already-saved
        // links (would violate the unique constraint) or re-attempt already
        // completed deletes.
        const addedToSend = (this.added_component_types || []).filter(ct => !this._sessionLinkedCtIds.has(ct.id));
        const removedToSend = (this.removed_component_types || []).filter(ct => !this._sessionUnlinkedCtIds.has(ct.id));

        // Short-circuit before trackComponentTypes so the no-change path makes
        // zero API calls (covers untouched dialog, add-then-remove cycle, and
        // "everything already reconciled in a prior Apply").
        if (addedToSend.length === 0 && removedToSend.length === 0) {
            return of(null);
        }

        this.notification.openInfo('Saving linked components...');

        return this.trackComponentTypes().pipe(
            concatMap(() => {
                // Capture link.id → component_type.id before saveMany mutates et_ct_list,
                // so we can attribute the deletedIds it returns back to CTs.
                const linkIdToCtId = new Map<string, string>();
                this.et_ct_list.forEach(link => {
                    const ctId = link.relationships?.component_type?.data?.id;
                    if (ctId && link.id) linkIdToCtId.set(link.id, ctId);
                });
                return this.relMapping.saveMany('event_type_component_type_map', 'event_type', this.event_type.id, 'component_type',
                    addedToSend, removedToSend, this.et_ct_list)
                    .pipe(tap((result: RelMappingResult) => {
                        this.et_ct_list.push(...result.successes.map(s => s.data));
                        if (result.deletedIds.length) {
                            const deletedSet = new Set(result.deletedIds);
                            this.et_ct_list = this.et_ct_list.filter(item => !deletedSet.has(item.id));
                        }
                        if (result.failures.length > 0) {
                            console.log("ERROR: EventTypeFormDialog (saveManyComponentTypes) ", result.failures);
                            this.notification.openError(`${result.successes.length} saved. ${result.failures.length} component-type link(s) failed to save. Please see the console for details.`);
                        }
                        result.successes.forEach(s => {
                            const ctId = s.data?.relationships?.component_type?.data?.id;
                            if (ctId) {
                                this._sessionLinkedCtIds.add(ctId);
                                this._sessionUnlinkedCtIds.delete(ctId);
                            }
                        });
                        result.deletedIds.forEach(linkId => {
                            const ctId = linkIdToCtId.get(linkId);
                            if (ctId) {
                                this._sessionUnlinkedCtIds.add(ctId);
                                this._sessionLinkedCtIds.delete(ctId);
                            }
                        });
                    }));
            })
        );
    }

    private _setComponentTypeFilters(eventTypeId: ModelID) {
        this.selected_component_types_filter = eventTypeId ? [getManyRelationWithIdFilter('event_types', eventTypeId)] : null;
        this.selected_component_types_reverse_filter = eventTypeId ? [{not: getManyRelationWithIdFilter('event_types', eventTypeId)}] : null;
    }

    goToTab(tabGroup: MatTabGroup, tab) {
        tabGroup.selectedIndex = tab;
    }

    onPropertyColumnWidthsChanged($event) {
        let json = merge(cloneDeep(this.event_type.attributes.json), $event);
        let eventTypeJsonOnly = {id: this.event_type.id, type: 'event_type', attributes: {json: json}};
        this.event_type.attributes.json = json;
        this.api.event_type.obsPatch(eventTypeJsonOnly).pipe(first(), catchError(err => {
            console.log('Column widths not saved');
            return of(null);
        })).subscribe();
    }

    onCloseClick(): void {
        // disableClose only blocks ESC/backdrop, not this direct close. Guard the
        // explicit Close button and the in-tab navigation links so a save in flight
        // can't tear down the dialog (which would leave the uncancelled stream
        // mutating a destroyed component and hand the parent a stale event type).
        if (this.saving) return;
        this.dialogRef.close(this.saved_event_type);
    }

    matSelectCompare = function (option, value): boolean {
        if (value) {
            return option.name === value.name;
        }
    };

    ngOnDestroy(): void {
        if (this.saving) {
            this.notification.openWarning(
                'You closed the dialog while a save was in progress. ' +
                'Some items may not have saved correctly.');
        }
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
